import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";

interface AuthShellProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  quote?: string;
  quoteAuthor?: string;
  logoSrc?: string;
  logoLabel?: string;
  backHref?: string;
  backLabel?: string;
  children: ReactNode;
  /** Right-panel surface tint */
  variant?: "ivory" | "surface";
}

export function AuthShell({
  eyebrow = "CONORBIT",
  title,
  subtitle,
  quote = "Run your media operation like an orbit, not a chaos.",
  quoteAuthor = "CONORBIT Charter",
  logoSrc = "/conorbit-logo-transparent.png",
  logoLabel,
  backHref = "/",
  backLabel = "Back to home",
  children,
  variant = "surface",
}: AuthShellProps) {
  return (
    <div className="min-h-screen w-full grid lg:grid-cols-[1.1fr_1fr] bg-background">
      {/* LEFT, Emerald art panel */}
      <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden gradient-emerald-radial text-ivory p-12">
        <div className="absolute inset-0 bg-grid-emerald opacity-40" aria-hidden />
        {/* Orbit ornament */}
        <div className="absolute -right-40 -bottom-40 w-[640px] h-[640px] rounded-full border border-accent/20 orbit-spin" aria-hidden>
          <div className="absolute inset-12 rounded-full border border-accent/15" />
          <div className="absolute inset-28 rounded-full border border-accent/10" />
          <span className="absolute top-1/2 -right-2 w-4 h-4 rounded-full bg-accent shadow-gold-glow" />
        </div>
        <div className="absolute -left-32 top-1/3 w-[420px] h-[420px] rounded-full border border-accent/10 orbit-spin-reverse" aria-hidden />

        <div className="relative z-10 flex items-center gap-3">
          <img src={logoSrc} alt="CONORBIT" className="h-9 w-auto" />
          {logoLabel && (
            <span className="text-xs uppercase tracking-[0.2em] text-accent-soft/80 border-l border-accent/30 pl-3">
              {logoLabel}
            </span>
          )}
        </div>

        <div className="relative z-10 max-w-md space-y-6 animate-fade-in">
          <div className="text-xs uppercase tracking-[0.3em] text-accent">{eyebrow}</div>
          <h2 className="font-display text-4xl xl:text-5xl leading-[1.05] text-ivory">
            Communications,
            <br />
            <span className="text-gradient-gold">in orbit.</span>
          </h2>
          <p className="text-justify text-ivory/75 text-[15px] leading-relaxed text-balance">
            The multi-institution backbone for institutional press operations, registry,
            events, accreditation, coverage intelligence and awards.
          </p>
        </div>

        <figure className="relative z-10 max-w-md border-l-2 border-accent/60 pl-4">
          <blockquote className="text-ivory/85 text-sm italic leading-relaxed">"{quote}"</blockquote>
          <figcaption className="text-accent-soft/80 text-xs mt-2 tracking-wide">{quoteAuthor}</figcaption>
        </figure>
      </aside>

      {/* RIGHT, form */}
      <main
        className={
          "relative flex flex-col px-6 py-8 lg:px-16 lg:py-12 " +
          (variant === "ivory" ? "bg-background" : "bg-card")
        }
      >
        <div className="flex items-center justify-between mb-8">
          <Link
            to={backHref}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {backLabel}
          </Link>
          <ThemeToggle />
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
          {/* Mobile-only logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/conorbit-logo.png" alt="CONORBIT" className="h-8" />
          </div>

          <div className="mb-8">
            <h1 className="font-display text-3xl lg:text-4xl text-foreground tracking-tight">{title}</h1>
            {subtitle && <p className="text-justify text-muted-foreground text-sm mt-2">{subtitle}</p>}
          </div>

          {children}
        </div>

        <footer className="mt-10 text-[11px] text-muted-foreground/70 text-center">
          © {new Date().getFullYear()} CONORBIT, Communications backbone for institutions.
        </footer>
      </main>
    </div>
  );
}

export default AuthShell;
